import type { Sfyr, SfyrAnyModel, SfyrResolver } from '@epihub/core/src/stringifyr/core';

export function createUseSfyr<
  Model extends SfyrAnyModel = SfyrAnyModel,
  Context extends Record<string, any> = Record<string, any>,
>(sfyr: Sfyr<Model, Context>, opts: {
  React: {
    useSyncExternalStore: (
      subscribe: (onStoreChange: () => void) => () => void,
      getSnapshot: () => number,
      getServerSnapshot?: () => number,
    ) => number;
  };
}): () => SfyrResolver<Model, Context> {
  const { useSyncExternalStore } = opts.React;

  // Subscribe to both database AND context changes
  const subscribe = (onStoreChange: () => void) => {
    const unsubDb = sfyr.db.subscribe(onStoreChange);
    const unsubCtx = sfyr.context.subscribe(onStoreChange);
    return () => {
      unsubDb();
      unsubCtx();
    };
  };

  // Combined version from both db and context
  const getSnapshot = () => sfyr.db.getVersion() + sfyr.context.getVersion();

  /**
   * React hook that returns a reactive sfyr resolver.
   * The component will re-render when the database or context changes.
   */
  return function useSfyr(): SfyrResolver<Model, Context> {
    // Subscribe to changes - component re-renders when db or context updates
    // Pass getSnapshot as getServerSnapshot too for SSR pre-rendering
    useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    // Return the sfyr resolver function (it reads from the reactive db)
    return sfyr as SfyrResolver<Model, Context>;
  };
}
